import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Wrench } from "lucide-react";
import { cn } from "../../lib/cn";
import { Markdown } from "../Markdown";

/**
 * ToolGateCorrection - a subtle inline notice that an action the answer
 * mentioned should be performed through a control / tool rather than described
 * in prose. Renderer for the K2 `tool_gate_correction` block, raised from the
 * runtime's `missing_tool_use` post-answer signal (ais-agent-runtime
 * `PostProcessSignals`, AISquare-Studio/ais-agent-runtime#115). The platform's
 * chat `BlockRenderer` passes the block's `props` straight through, so the prop
 * names match the block payload exactly. DISPLAY-ONLY (no submit).
 *
 * When the post-processor notices the answer narrated a mutation ("I've
 * published it", "I scheduled the post") but the turn proposed no corresponding
 * tool / action, it does NOT rewrite the answer here - the runtime's own
 * no-fabrication downgrade handles the wording. This block is the gentle,
 * user-facing footnote pointing the user at the real control: "to actually do
 * that, use the Publish button." It is a hint, not an error: muted surface,
 * thin border, no destructive colour.
 *
 * Block props (`{ message }`):
 *   - `message` - the correction copy. Rendered as markdown so it can carry a
 *     short emphasis or inline link to the control. Required.
 *
 * Client-only props (never part of the streamed block): `className`, `icon`
 * (override / hide the default wrench icon).
 *
 * Token-themed with no hardcoded colors, on the dashed-footnote take of the
 * shared block surface (`blockSurface.ts`, #176/#180). A11y: `role="note"` + an
 * `sr-only` "Tip:" prefix so the footnote reads in context. SSR-safe: no
 * `window` / `document` access.
 */

const toolGateCorrectionVariants = cva(
  cn(
    // The dashed-footnote take on the shared block surface (#176/#180): the
    // same rounded-2xl radius + inset the other blocks read, with the dashed
    // muted treatment UnsupportedBlock uses for "aside, not content".
    "not-prose flex items-start gap-2 rounded-2xl border border-dashed border-border bg-muted/30 p-4 text-xs",
    "text-muted-foreground",
    "[&_a]:font-medium [&_a]:text-foreground [&_a]:underline [&_a]:underline-offset-2",
    "[&_strong]:font-medium [&_strong]:text-foreground",
  ),
);

export interface ToolGateCorrectionProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof toolGateCorrectionVariants> {
  /** The correction copy. Rendered as markdown. Required. */
  message: string;
  /** Override the default wrench icon. Pass `null` to hide it. */
  icon?: React.ReactNode;
}

export const ToolGateCorrection = React.forwardRef<HTMLDivElement, ToolGateCorrectionProps>(
  function ToolGateCorrection({ className, message, icon, ...rest }, ref) {
    return (
      <div
        ref={ref}
        role="note"
        className={cn(toolGateCorrectionVariants(), className)}
        data-slot="tool-gate-correction"
        {...rest}
      >
        {icon === null ? null : (
          <span className="mt-0.5 shrink-0 text-muted-foreground" aria-hidden>
            {icon ?? <Wrench className="size-3.5" />}
          </span>
        )}
        <div className="min-w-0 flex-1 [&_p]:leading-relaxed [&_p:last-child]:mb-0">
          <span className="sr-only">Tip: </span>
          <Markdown content={message} />
        </div>
      </div>
    );
  },
);

ToolGateCorrection.displayName = "ToolGateCorrection";

export { toolGateCorrectionVariants };
export type ToolGateCorrectionVariantProps = VariantProps<typeof toolGateCorrectionVariants>;
